import { useEffect, useRef, useState } from 'react';
import { normalizeTransferCode } from '@brainfart/shared';
import { Sheet, SheetBody } from './Sheet';
import { isIos, isStandalone } from '../installPrompt';
import { claimRestoreCode, declineRestore, restoreDeclined, hasProgress } from '../profileTransfer';
import { track } from '../telemetry';

// The iOS home-screen app starts with empty storage. On its first launch, ask
// once for the restore code the install steps showed in Safari.
function shouldOffer(): boolean {
  return isStandalone() && isIos() && !hasProgress() && !restoreDeclined();
}

export function RestoreSheet() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [state, setState] = useState<'idle' | 'working' | 'invalid' | 'error'>('idle');
  const offered = useRef(false);

  useEffect(() => {
    if (offered.current || !shouldOffer()) return;
    offered.current = true;
    setOpen(true);
    track({ type: 'restore_offered', payload: {} });
  }, []);

  const valid = normalizeTransferCode(text) !== null;

  const restore = async () => {
    if (!valid || state === 'working') return;
    setState('working');
    const result = await claimRestoreCode(text);
    if (result === 'ok') {
      track({ type: 'restore_done', payload: {} });
      // Everything reads its storage at startup, so start over with the restored profile.
      window.location.replace('/');
      return;
    }
    track({ type: 'restore_failed', payload: { result } });
    setState(result);
  };

  const paste = async () => {
    try {
      const clip = await navigator.clipboard.readText();
      if (clip) { setText(clip.trim().slice(0, 20)); setState('idle'); }
    } catch { /* they can type it */ }
  };

  const startFresh = () => {
    declineRestore();
    track({ type: 'restore_declined', payload: {} });
    setOpen(false);
  };

  return (
    <Sheet open={open} onClose={startFresh} label="Restore your progress">
      <SheetBody
        icon="🔑"
        title="Bring your progress?"
        body="Played BrainFart in Safari? Enter the restore code from the install steps and your streak, ratings and player card come along."
      >
        <input
          value={text}
          onChange={e => { setText(e.target.value.toUpperCase().slice(0, 12)); setState('idle'); }}
          onKeyDown={e => { if (e.key === 'Enter') restore(); }}
          placeholder="XXXX-XXXX"
          aria-label="Restore code"
          autoCapitalize="characters"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="go"
          style={{
            width: '100%', padding: '0.85rem 1rem', borderRadius: 12, border: '1px solid #2E2B45', background: '#100E22', color: '#FFC93C',
            fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '1.3rem', fontWeight: 800, letterSpacing: '0.12em', textAlign: 'center', outline: 'none',
          }}
        />
        {state === 'invalid' && <p role="alert" style={{ color: '#FF6B6B', fontSize: '0.85rem' }}>That code didn't work. It may have been used already or expired. Codes last two days.</p>}
        {state === 'error' && <p role="alert" style={{ color: '#FF6B6B', fontSize: '0.85rem' }}>Couldn't reach BrainFart. Check your connection and try again.</p>}
        <button className="btn btn-primary" onClick={restore} disabled={!valid || state === 'working'}>
          {state === 'working' ? 'Restoring…' : 'Restore my progress'}
        </button>
        <button className="btn btn-ghost" onClick={paste}>📋 Paste from clipboard</button>
        <button className="btn btn-ghost" onClick={startFresh}>Start fresh</button>
      </SheetBody>
    </Sheet>
  );
}
